import { IEnv } from '../../../infrastructure/i-env';
import { Language } from '../../../dto/language';
import { TranslationMode } from '../../../dto/translation-mode';
import { IUserPreferencesService } from '../../../services/i-user-preferences-service';
import type { EnabledLanguagePairDto, UserPreferencesDto } from '../../../dto/user-preferences-dto';

export type IntroLevel = 'beginner' | 'intermediate';

export interface IntroLanguageOption {
	code: Language;
	name: string;
}

export interface IntroPreferences {
	nativeLanguage: Language;
	learnedLanguage: Language;
	mode: TranslationMode;
	newsInterests: string;
	dateTimeOffset?: number | null;
	timestamp: number;
}

export interface IntroScreenState {
	selectedNativeLanguage: Language | null;
	selectedLearnLanguage: Language | null;
	selectedLevel: IntroLevel | null;
	newsInterests: string;

	nativeLanguages: IntroLanguageOption[];
	allLearnLanguages: IntroLanguageOption[];
	learnLanguages: IntroLanguageOption[];
}

const DEFAULT_LANGUAGE_OPTIONS: IntroLanguageOption[] = [
	{ code: Language.EN, name: 'English' },
	{ code: Language.FR, name: 'French' },
	{ code: Language.DE, name: 'German' },
	{ code: Language.ES, name: 'Spanish' },
	{ code: Language.IT, name: 'Italian' },
	{ code: Language.RU, name: 'Russian' },
	{ code: Language.HY, name: 'Armenian' },
	{ code: Language.HI, name: 'Hindi' },
	{ code: Language.BN, name: 'Bengali' }
];
const DEFAULT_DAILY_WORD_COUNT_TARGET = 10;

export abstract class IIntroScreenViewModel {
	abstract readonly state: IntroScreenState;

	abstract init(): Promise<void>;
	abstract getLanguageName(code: Language | null): string;
	abstract handleStart(): Promise<void>;
}

export class IntroScreenViewModel extends IIntroScreenViewModel {
	private readonly storageKey = 'lingvo-news-preferences';
	private enabledLanguagePairs: readonly EnabledLanguagePairDto[] = [];
	public readonly state: IntroScreenState;

	constructor(
		private readonly env: IEnv,
		private readonly setLocale: (locale: string) => void
	) {
		super();

		this.state = env.reactive({
			selectedNativeLanguage: null,
			selectedLearnLanguage: null,
			selectedLevel: null,
			newsInterests: '',

			nativeLanguages: [...DEFAULT_LANGUAGE_OPTIONS],
			allLearnLanguages: [...DEFAULT_LANGUAGE_OPTIONS],
			learnLanguages: []
		}) as IntroScreenState;

		this.recomputeDerived();

		env.watch(
			() => this.state.selectedNativeLanguage,
			(newNative) => {
				if (newNative) {
					// Language enum values align with supported vue-i18n locale keys (en/fr/de/es/ru/it/hy)
					this.setLocale(newNative);
				}

				if (newNative && this.state.selectedLearnLanguage === newNative) {
					this.state.selectedLearnLanguage = null;
				}

				this.recomputeDerived();
			}
		);
	}

	async init(): Promise<void> {
		await this.loadEnabledLanguagePairs();
		await this.env.currentUser.initialize();

		const userId = this.env.currentUser.getUserId();
		if (this.env.currentUser.isAuthenticated() && userId) {
			try {
				const userService = this.env.get(IUserPreferencesService);
				const remote = await userService.getUserPreferences(userId);
				if (remote) {
					this.applyDto(remote);
					return;
				}
			} catch (e) {
				this.env.log('Failed to load user preferences', e);
			}
		}

		const saved = this.env.clientStorage.get<UserPreferencesDto>(this.storageKey);
		if (saved) {
			this.applyDto(saved);
			return;
		}
	}

	getLanguageName(code: Language | null): string {
		if (!code) return '';
		const all = [...this.state.nativeLanguages, ...this.state.allLearnLanguages];
		const found = all.find(l => l.code === code);
		return found ? found.name : code;
	}

	async handleStart(): Promise<void> {
		if (!this.state.selectedNativeLanguage || !this.state.selectedLearnLanguage || !this.state.selectedLevel) {
			return;
		}

		// Intro wizard always starts users in partial mode.
		const mode = TranslationMode.Partial;
		const interests = this.state.newsInterests.trim();
		const existing = this.env.clientStorage.get<UserPreferencesDto>(this.storageKey);
		const dailyWordCountTarget = existing?.dailyWordCountTarget ?? DEFAULT_DAILY_WORD_COUNT_TARGET;
		const dateTimeOffset = new Date().getTimezoneOffset();

		const preferences: UserPreferencesDto = {
			nativeLanguage: this.state.selectedNativeLanguage,
			learnedLanguage: this.state.selectedLearnLanguage,
			mode,
			interests,
			dailyWordCountTarget,
			dateTimeOffset,
			timestamp: Date.now()
		};

		this.env.clientStorage.set(this.storageKey, preferences);

		const userId = this.env.currentUser.getUserId();
		if (this.env.currentUser.isAuthenticated() && userId) {
			try {
				const userService = this.env.get(IUserPreferencesService);
				await userService.setUserPreferences(userId, preferences);
			} catch (e) {
				this.env.log('Failed to save user preferences', e);
			}
		}

		await this.env.routing.navigate(
				this.env.routing.getNewsListLink(preferences.nativeLanguage, preferences.learnedLanguage, mode)
		);
	}

	private applyDto(dto: UserPreferencesDto): void {
		this.state.selectedNativeLanguage = dto.nativeLanguage ?? null;
		this.state.selectedLearnLanguage = dto.learnedLanguage ?? null;
		this.state.newsInterests = dto.interests ?? '';
		this.state.selectedLevel = dto.mode === TranslationMode.Partial ? 'beginner' : 'intermediate';
		this.recomputeDerived();
	}

	private recomputeDerived(): void {
		const strictFullModePairs = this.enabledLanguagePairs.filter((pair) => pair.mode === TranslationMode.Full);
		const pairsForFullMode = strictFullModePairs.length > 0 ? strictFullModePairs : this.enabledLanguagePairs;
		if (this.state.selectedLevel === 'intermediate' && pairsForFullMode.length > 0) {
			const nativeCodes = pairsForFullMode.map((pair) => pair.target);
			this.state.nativeLanguages = this.toLanguageOptions(nativeCodes);
			this.state.allLearnLanguages = this.toLanguageOptions(pairsForFullMode.map((pair) => pair.source));

			if (this.state.selectedNativeLanguage && !nativeCodes.includes(this.state.selectedNativeLanguage)) {
				this.state.selectedNativeLanguage = null;
			}

			if (!this.state.selectedNativeLanguage) {
				this.state.learnLanguages = [];
				if (this.state.selectedLearnLanguage) {
					this.state.selectedLearnLanguage = null;
				}
				return;
			}

			const learnCodes = pairsForFullMode
				.filter((pair) => pair.target === this.state.selectedNativeLanguage)
				.map((pair) => pair.source);
			this.state.learnLanguages = this.toLanguageOptions(learnCodes);

			if (this.state.selectedLearnLanguage && !learnCodes.includes(this.state.selectedLearnLanguage)) {
				this.state.selectedLearnLanguage = null;
			}
			return;
		}

		this.state.nativeLanguages = [...DEFAULT_LANGUAGE_OPTIONS];
		this.state.allLearnLanguages = [...DEFAULT_LANGUAGE_OPTIONS];
		this.state.learnLanguages = this.state.allLearnLanguages.filter(l => l.code !== this.state.selectedNativeLanguage);
		if (this.state.selectedLearnLanguage && !this.state.learnLanguages.some((l) => l.code === this.state.selectedLearnLanguage)) {
			this.state.selectedLearnLanguage = null;
		}
	}

	private toLanguageOptions(codes: readonly Language[]): IntroLanguageOption[] {
		const defaultNameByCode = new Map(DEFAULT_LANGUAGE_OPTIONS.map((option) => [option.code, option.name] as const));
		const seen = new Set<Language>();
		const result: IntroLanguageOption[] = [];
		for (const code of codes) {
			if (seen.has(code)) {
				continue;
			}
			seen.add(code);
			result.push({
				code,
				name: defaultNameByCode.get(code) ?? code
			});
		}
		return result;
	}

	private async loadEnabledLanguagePairs(): Promise<void> {
		try {
			const userService = this.env.get(IUserPreferencesService);
			this.enabledLanguagePairs = await userService.getEnabledLanguagePairs();
		} catch (e) {
			this.env.log('Failed to load enabled language pairs', e);
			this.enabledLanguagePairs = [];
		}
		this.recomputeDerived();
	}

	public format(): string {
		return [
			'IntroScreenViewModel',
			`selectedNativeLanguage: ${this.state.selectedNativeLanguage ?? ''}`,
			`selectedLearnLanguage: ${this.state.selectedLearnLanguage ?? ''}`,
			`selectedLevel: ${this.state.selectedLevel ?? ''}`,
			`newsInterests: ${this.state.newsInterests}`,
			`learnLanguagesCount: ${this.state.learnLanguages.length}`
		].join('\n');
	}
}
